import { Component, ComponentType, HTMLAttributes } from 'react';
import { WrappedFieldProps } from 'redux-form';
import { FONT_SIZES, SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import FormControlProps from 'lib/interfaces/FormControlProps';

const Container = styled('div')`
  margin-bottom: ${SPACINGS.base * 2}px;
`;

export const FormError = styled('div')`
  color: ${({ theme }) => theme.colorError};
  font-size: ${FONT_SIZES.small};
  margin-top: ${SPACINGS.base}px;
  white-space: pre-line;
`;

const getName = (component: ComponentType<any>) => {
  if (typeof component === 'string') {
    return component;
  }

  return (component as any).displayName || 'Component';
};

// These props need to be passed through the Field into the FormControl.
export interface FieldThroughFormControlProps {
  autoFocus?: boolean;
  id?: string;
}

export type WrappedFormControlProps = WrappedFieldProps & FieldThroughFormControlProps;

export default function FormControl<P = HTMLAttributes<{}>>(
  WrappedComponent: ComponentType<FormControlProps & P>,
): ComponentType<WrappedFormControlProps & P> {
  return class extends Component<WrappedFormControlProps & P> {
    public static displayName = `FormControl(${getName(WrappedComponent)})`;

    constructor(props: WrappedFormControlProps & P) {
      super(props);
    }

    public render() {
      const {
        className,
        input,
        meta: { error, warning, submitFailed, touched },
        autoFocus,
        // NOTE: Rest operator doesn't work without any cast
        ...passthroughProps
      } = this.props as any;

      const hasError = !!((error || warning) && (submitFailed || touched));

      return (
        <Container className={className}>
          <WrappedComponent
            {...input}
            {...passthroughProps}
            hasError={hasError}
            warning={warning}
            id={this.id}
          />
          {hasError && (
            <FormError id={this.errorId} role='alert'>
              {error}
            </FormError>
          )}
        </Container>
      );
    }

    /**
     * Default to <Field/> name, which should be locally unique amongst a form.
     * This is primarily used for labels
     * The component's props.id should be manually passed for the following edge cases:
     * - where two forms are visible at the same time and have a field with the same name
     * - radio inputs share the same name
     */
    private get id(): string {
      const {
        id,
        input: { name },
      } = this.props;

      return id || name;
    }

    private get errorId(): string {
      return `${this.id}--error`;
    }
  };
}
